// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ProgressIndicator,
  ProgressStep,
  TextInput,
  Select,
  SelectItem,
  FileUploaderDropContainer,
  FileUploaderItem,
  Accordion,
  AccordionItem,
  Tag,
  Search,
  Tile,
  CheckboxGroup,
  Checkbox,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import { Add, TrashCan, Close, ArrowRight, ArrowLeft } from '@carbon/icons-react';
import { getUsers } from '../services/userService';
import { createSite, listRegions } from '../services/omsService';
import { useAuth } from '../hooks/useAuth';
import { EmptyState } from '../components/shell/ScreenHelpers';
import type { OmsRegion, OmsSiteCreate } from '../types';
import './OnboardSite.css';

interface Step {
  id: number;
  name: string;
}

interface NewUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface ExistingUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  site_ids: string[];
}

interface TelemetrySource {
  id: number;
  type: string;
  name: string;
  endpoint: string;
  protocol: string;
}

interface OnsiteGeneration {
  id: number;
  type: string;
  capacityKW: string;
}

interface GridConnection {
  id: number;
  name: string;
  capacityKW: string;
}

interface OntologyExpanded {
  knowledgeGraph: boolean;
  electrical: boolean;
  mechanical: boolean;
  operational: boolean;
}

interface Notice {
  kind: 'error' | 'success' | 'info' | 'warning';
  title: string;
  subtitle?: string;
}

interface FormData {
  // Overview
  name: string;
  siteKey: string;
  regionId: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  redundancyLevel: string;
  loadCapacity: string;
  utilityProvider: string;
  utilityAccountNumber: string;
  slaUptimePercentage: string;
  slaDocument: File | null;
  // People
  newUsers: NewUser[];
  existingUserAccess: ExistingUser[];
  // Ontology - Knowledge Graph
  mapFile: File | null;
  // Ontology - Electrical Systems
  electricalOneLine: (File | string)[];
  electricalBOM: File | null;
  panelSchedules: (File | string)[];
  // Ontology - Mechanical Systems
  mechanicalLayout: (File | string)[];
  hvacDetails: (File | string)[];
  // Ontology - Operational Procedures
  sops: (File | string)[];
  maintenanceProcedures: (File | string)[];
  // Telemetry
  telemetrySources: TelemetrySource[];
  // Checks
  onsiteGeneration: OnsiteGeneration[];
  gridConnections: GridConnection[];
  optimizationGoals: string[];
  // Confirm
}

type FileArrayField =
  | 'electricalOneLine'
  | 'panelSchedules'
  | 'mechanicalLayout'
  | 'hvacDetails'
  | 'sops'
  | 'maintenanceProcedures';

type SingleFileField = 'slaDocument' | 'mapFile' | 'electricalBOM';

const DRAFT_STORAGE_KEY = 'helios_onboarding_draft';

function OnboardSite() {
  const navigate = useNavigate();
  const { meResponse } = useAuth();
  const orgId = meResponse?.organization?.id ?? null;
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [allExistingUsers, setAllExistingUsers] = useState<ExistingUser[]>([]);
  const [displayedUsers, setDisplayedUsers] = useState<ExistingUser[]>([]);
  const [selectedExistingUsers, setSelectedExistingUsers] = useState<ExistingUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [regions, setRegions] = useState<OmsRegion[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [ontologyExpanded, setOntologyExpanded] = useState<OntologyExpanded>({
    knowledgeGraph: true,
    electrical: false,
    mechanical: false,
    operational: false,
  });
  const [formData, setFormData] = useState<FormData>({
    // Overview
    name: '',
    siteKey: '',
    regionId: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    redundancyLevel: '',
    loadCapacity: '',
    utilityProvider: '',
    utilityAccountNumber: '',
    slaUptimePercentage: '',
    slaDocument: null,
    // People
    newUsers: [],
    existingUserAccess: [],
    // Ontology - Knowledge Graph
    mapFile: null,
    // Ontology - Electrical Systems
    electricalOneLine: [],
    electricalBOM: null,
    panelSchedules: [],
    // Ontology - Mechanical Systems
    mechanicalLayout: [],
    hvacDetails: [],
    // Ontology - Operational Procedures
    sops: [],
    maintenanceProcedures: [],
    // Telemetry
    telemetrySources: [],
    // Checks
    onsiteGeneration: [],
    gridConnections: [],
    optimizationGoals: [],
    // Confirm
  });

  const steps: Step[] = [
    { id: 0, name: 'Overview' },
    { id: 1, name: 'People' },
    { id: 2, name: 'Ontology' },
    { id: 3, name: 'Telemetry' },
    { id: 4, name: 'Checks' },
    { id: 5, name: 'Confirm' },
  ];

  // Save draft to localStorage
  const saveDraft = useCallback(() => {
    if (!formData.name) return;

    setIsSaving(true);
    try {
      const draft = {
        currentStep,
        formData: {
          ...formData,
          slaDocument: formData.slaDocument?.name || null,
          mapFile: formData.mapFile?.name || null,
          electricalBOM: formData.electricalBOM?.name || null,
          electricalOneLine: formData.electricalOneLine.map(f => (f instanceof File ? f.name : f)),
          panelSchedules: formData.panelSchedules.map(f => (f instanceof File ? f.name : f)),
          mechanicalLayout: formData.mechanicalLayout.map(f => (f instanceof File ? f.name : f)),
          hvacDetails: formData.hvacDetails.map(f => (f instanceof File ? f.name : f)),
          sops: formData.sops.map(f => (f instanceof File ? f.name : f)),
          maintenanceProcedures: formData.maintenanceProcedures.map(f =>
            f instanceof File ? f.name : f
          ),
        },
        selectedExistingUserIds: selectedExistingUsers.map(u => u.id),
      };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setIsSaving(false);
    }
  }, [currentStep, formData, selectedExistingUsers]);

  // Fetch users and regions on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const users = await getUsers();
        setAllExistingUsers(users as unknown as ExistingUser[]);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setAllExistingUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!orgId) return;
    const fetchRegions = async () => {
      try {
        const r = await listRegions(orgId);
        setRegions(r);
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
    };
    fetchRegions();
  }, [orgId]);

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);

      if (draft.formData) {
        setFormData(prev => ({
          ...prev,
          ...draft.formData,
          // File objects can't be serialized — restore as null
          slaDocument: null,
          mapFile: null,
          electricalBOM: null,
        }));
      }

      if (draft.currentStep != null) {
        setCurrentStep(draft.currentStep);
      }

      if (draft.selectedExistingUserIds?.length > 0) {
        const loadSelectedUsers = async () => {
          try {
            const users = await getUsers();
            const selected = (users as unknown as ExistingUser[]).filter(u =>
              draft.selectedExistingUserIds.includes(u.id)
            );
            setSelectedExistingUsers(selected);
          } catch {
            // ignore — users will just not be pre-selected
          }
        };
        loadSelectedUsers();
      }

      setLastSaved(new Date());
    } catch {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
  }, []);

  // Filter users based on search query
  useEffect(() => {
    // Filter by search query if present
    let filtered = allExistingUsers;
    if (userSearchQuery.trim() !== '') {
      const query = userSearchQuery.toLowerCase();
      filtered = allExistingUsers.filter(user => {
        const name = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
        const email = user.email?.toLowerCase() || '';
        const role = user.role?.toLowerCase() || '';
        return name.includes(query) || email.includes(query) || role.includes(query);
      });
    }

    // Exclude already selected users
    const unselectedUsers = filtered.filter(
      user => !selectedExistingUsers.some(selected => selected.id === user.id)
    );

    setDisplayedUsers(unselectedUsers);
  }, [userSearchQuery, allExistingUsers, selectedExistingUsers]);

  const updateFormData = <K extends keyof FormData>(field: K, value: FormData[K]): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateOverviewStep = (): boolean => {
    const requiredFields: (keyof FormData)[] = [
      'name',
      'siteKey',
      'regionId',
      'address',
      'city',
      'state',
      'zipCode',
      'country',
      'redundancyLevel',
      'loadCapacity',
      'utilityProvider',
      'slaUptimePercentage',
    ];

    for (const field of requiredFields) {
      if (!formData[field] || formData[field] === '') {
        return false;
      }
    }

    if (!formData.slaDocument) {
      return false;
    }

    return true;
  };

  const handleNext = (): void => {
    if (currentStep === 0 && !validateOverviewStep()) {
      setNotice({
        kind: 'error',
        title: 'Missing required fields',
        subtitle: 'Please fill in all required fields before proceeding.',
      });
      return;
    }

    setNotice(null);
    saveDraft();

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = (): void => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Allow jumping only to a completed/current step, or one step forward
  // (which runs the same guard + save as handleNext when leaving Overview).
  const handleStepJump = (index: number): void => {
    if (index === currentStep) return;

    if (index < currentStep) {
      setCurrentStep(index);
      return;
    }

    if (index === currentStep + 1) {
      if (currentStep === 0 && !validateOverviewStep()) {
        setNotice({
          kind: 'error',
          title: 'Missing required fields',
          subtitle: 'Please fill in all required fields before proceeding.',
        });
        return;
      }
      setNotice(null);
      saveDraft();
      setCurrentStep(index);
    }
    // steps more than one ahead are disabled — ignore
  };

  const handleSubmit = async (): Promise<void> => {
    if (!orgId) {
      setNotice({
        kind: 'error',
        title: 'No organization found',
        subtitle: 'Please ensure you are logged in.',
      });
      return;
    }

    if (!formData.siteKey || !formData.regionId || !formData.name) {
      setNotice({
        kind: 'error',
        title: 'Missing required fields',
        subtitle: 'Please complete the required fields (site key, region, and name).',
      });
      return;
    }

    try {
      const attributes: Record<string, unknown> = {
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        zipCode: formData.zipCode || null,
        country: formData.country || 'USA',
        redundancyLevel: formData.redundancyLevel || null,
        loadCapacity: formData.loadCapacity ? parseFloat(formData.loadCapacity) : null,
        utilityProvider: formData.utilityProvider || null,
        utilityAccountNumber: formData.utilityAccountNumber || null,
        slaUptimePercentage: formData.slaUptimePercentage
          ? parseFloat(formData.slaUptimePercentage)
          : null,
        slaDocumentName: formData.slaDocument?.name || null,
        ontology: {
          mapFile: formData.mapFile?.name || null,
          electricalOneLine: formData.electricalOneLine.map(f => (f instanceof File ? f.name : f)),
          electricalBOM: formData.electricalBOM?.name || null,
          panelSchedules: formData.panelSchedules.map(f => (f instanceof File ? f.name : f)),
          mechanicalLayout: formData.mechanicalLayout.map(f => (f instanceof File ? f.name : f)),
          hvacDetails: formData.hvacDetails.map(f => (f instanceof File ? f.name : f)),
          sops: formData.sops.map(f => (f instanceof File ? f.name : f)),
          maintenanceProcedures: formData.maintenanceProcedures.map(f =>
            f instanceof File ? f.name : f
          ),
        },
        telemetrySources: formData.telemetrySources,
        checks: {
          onsiteGeneration: formData.onsiteGeneration,
          gridConnections: formData.gridConnections,
          optimizationGoals: formData.optimizationGoals,
        },
        people: {
          newUsers: formData.newUsers,
          existingUserIds: selectedExistingUsers.map(u => u.id),
        },
      };

      const payload: OmsSiteCreate = {
        siteKey: formData.siteKey,
        regionId: formData.regionId,
        label: formData.name,
        attributes,
      };

      await createSite(orgId, payload);

      localStorage.removeItem(DRAFT_STORAGE_KEY);

      navigate('/admin');
    } catch (error) {
      console.error('Onboarding failed:', error);
      setNotice({
        kind: 'error',
        title: 'Onboarding failed',
        subtitle: 'Failed to complete onboarding: ' + (error as Error).message,
      });
    }
  };

  const handleSaveAndExit = (): void => {
    // Persist the draft, then leave. We navigate away immediately, so an inline
    // notification here would never be seen (the component unmounts) — the draft
    // simply resumes from this step on the next visit.
    saveDraft();
    navigate('/admin');
  };

  const formatLastSaved = (date: Date | null): string => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;

    return date.toLocaleDateString();
  };

  const addTelemetrySource = (): void => {
    const newSource: TelemetrySource = {
      id: Date.now(),
      type: 'dump',
      name: '',
      endpoint: '',
      protocol: 'https',
    };
    updateFormData('telemetrySources', [...formData.telemetrySources, newSource]);
  };

  const updateTelemetrySource = (id: number, field: keyof TelemetrySource, value: string): void => {
    const updated = formData.telemetrySources.map(source =>
      source.id === id ? { ...source, [field]: value } : source
    );
    updateFormData('telemetrySources', updated);
  };

  const removeTelemetrySource = (id: number): void => {
    updateFormData(
      'telemetrySources',
      formData.telemetrySources.filter(s => s.id !== id)
    );
  };

  const addNewUser = (): void => {
    const newUser: NewUser = {
      id: Date.now(),
      email: '',
      firstName: '',
      lastName: '',
      role: 'operator',
    };
    updateFormData('newUsers', [...formData.newUsers, newUser]);
  };

  const updateNewUser = (id: number, field: keyof NewUser, value: string): void => {
    const updated = formData.newUsers.map(user =>
      user.id === id ? { ...user, [field]: value } : user
    );
    updateFormData('newUsers', updated);
  };

  const removeNewUser = (id: number): void => {
    updateFormData(
      'newUsers',
      formData.newUsers.filter(u => u.id !== id)
    );
  };

  const addExistingUser = (user: ExistingUser): void => {
    setSelectedExistingUsers(prev => [...prev, user]);
  };

  const removeExistingUser = (userId: string): void => {
    setSelectedExistingUsers(prev => prev.filter(u => u.id !== userId));
  };

  const toggleOntologySection = (section: keyof OntologyExpanded): void => {
    setOntologyExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const addOnsiteGeneration = (): void => {
    updateFormData('onsiteGeneration', [
      ...formData.onsiteGeneration,
      { id: Date.now(), type: '', capacityKW: '' },
    ]);
  };

  const updateOnsiteGeneration = (
    id: number,
    field: keyof OnsiteGeneration,
    value: string
  ): void => {
    const updated = formData.onsiteGeneration.map(gen =>
      gen.id === id ? { ...gen, [field]: value } : gen
    );
    updateFormData('onsiteGeneration', updated);
  };

  const removeOnsiteGeneration = (id: number): void => {
    updateFormData(
      'onsiteGeneration',
      formData.onsiteGeneration.filter(gen => gen.id !== id)
    );
  };

  const addGridConnection = (): void => {
    updateFormData('gridConnections', [
      ...formData.gridConnections,
      { id: Date.now(), name: '', capacityKW: '' },
    ]);
  };

  const updateGridConnection = (id: number, field: keyof GridConnection, value: string): void => {
    const updated = formData.gridConnections.map(conn =>
      conn.id === id ? { ...conn, [field]: value } : conn
    );
    updateFormData('gridConnections', updated);
  };

  const removeGridConnection = (id: number): void => {
    updateFormData(
      'gridConnections',
      formData.gridConnections.filter(conn => conn.id !== id)
    );
  };

  const addMultipleFiles = (field: FileArrayField, files: File[]): void => {
    if (!files || files.length === 0) return;
    updateFormData(field, [...(formData[field] || []), ...files]);
  };

  const removeFileFromList = (field: FileArrayField, index: number): void => {
    const updated = [...formData[field]];
    updated.splice(index, 1);
    updateFormData(field, updated);
  };

  const toggleOptimizationGoal = (goal: string, checked: boolean): void => {
    const goals = checked
      ? [...formData.optimizationGoals, goal]
      : formData.optimizationGoals.filter(g => g !== goal);
    updateFormData('optimizationGoals', goals);
  };

  // Multi-file upload block bound to a (File | string)[] formData field.
  const renderMultiFileField = (field: FileArrayField, accept: string[]): React.ReactNode => {
    const files = formData[field];
    return (
      <>
        <FileUploaderDropContainer
          labelText="Drag and drop files here or click to upload"
          accept={accept}
          multiple
          onAddFiles={(
            _e: React.SyntheticEvent<HTMLElement>,
            { addedFiles }: { addedFiles: File[] }
          ) => addMultipleFiles(field, addedFiles)}
        />
        {files.length > 0 && (
          <div className="obs-file-list">
            {files.map((f, idx) => (
              <FileUploaderItem
                key={idx}
                name={f instanceof File ? f.name : f}
                status="edit"
                onDelete={() => removeFileFromList(field, idx)}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  // Single-file upload block bound to a File | null formData field.
  const renderSingleFileField = (field: SingleFileField, accept: string[]): React.ReactNode => {
    const file = formData[field];
    return (
      <>
        <FileUploaderDropContainer
          labelText="Drag and drop a file here or click to upload"
          accept={accept}
          onAddFiles={(
            _e: React.SyntheticEvent<HTMLElement>,
            { addedFiles }: { addedFiles: File[] }
          ) => updateFormData(field, addedFiles[0] ?? null)}
        />
        {file && (
          <div className="obs-file-list">
            <FileUploaderItem
              name={file.name}
              status="edit"
              onDelete={() => updateFormData(field, null)}
            />
          </div>
        )}
      </>
    );
  };

  const renderStepContent = (): React.ReactNode => {
    switch (currentStep) {
      case 0: // Overview
        return (
          <div className="obs-step">
            <h2 className="obs-step-title">Site Overview</h2>
            <p className="obs-step-desc">Provide basic information about the new site</p>

            <div className="obs-form-grid">
              <div className="obs-field">
                <TextInput
                  id="site-name"
                  labelText="Site Name *"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('name', e.target.value)
                  }
                  placeholder="e.g., Headquarters Site"
                />
              </div>

              <div className="obs-field">
                <TextInput
                  id="site-key"
                  labelText="Site Key *"
                  helperText="Unique kebab-case identifier for this site"
                  value={formData.siteKey}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData(
                      'siteKey',
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                    )
                  }
                  placeholder="e.g., hq-dc"
                />
              </div>

              <div className="obs-field">
                <Select
                  id="region"
                  labelText="Region *"
                  value={formData.regionId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    updateFormData('regionId', e.target.value)
                  }
                >
                  <SelectItem value="" text="Select region..." />
                  {regions.map(r => (
                    <SelectItem key={r.regionId} value={r.regionId} text={r.label} />
                  ))}
                </Select>
              </div>

              <div className="obs-field">
                <Select
                  id="redundancy"
                  labelText="Redundancy Level *"
                  value={formData.redundancyLevel}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    updateFormData('redundancyLevel', e.target.value)
                  }
                >
                  <SelectItem value="" text="Select redundancy level..." />
                  <SelectItem value="N" text="N (No redundancy)" />
                  <SelectItem value="N+1" text="N+1 (Single redundant component)" />
                  <SelectItem value="N+2" text="N+2 (Dual redundant components)" />
                  <SelectItem value="2N" text="2N (Fully redundant system)" />
                  <SelectItem value="2N+1" text="2N+1 (Fully redundant + one extra)" />
                </Select>
              </div>

              <div className="obs-field obs-full">
                <TextInput
                  id="address"
                  labelText="Street Address *"
                  value={formData.address}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('address', e.target.value)
                  }
                  placeholder="e.g., 1234 Main Street"
                />
              </div>

              <div className="obs-field">
                <TextInput
                  id="city"
                  labelText="City *"
                  value={formData.city}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('city', e.target.value)
                  }
                  placeholder="e.g., Austin"
                />
              </div>

              <div className="obs-field">
                <TextInput
                  id="state"
                  labelText="State/Province *"
                  value={formData.state}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('state', e.target.value)
                  }
                  placeholder="e.g., TX"
                />
              </div>

              <div className="obs-field">
                <TextInput
                  id="zip"
                  labelText="ZIP/Postal Code *"
                  value={formData.zipCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('zipCode', e.target.value)
                  }
                  placeholder="e.g., 78701"
                />
              </div>

              <div className="obs-field">
                <Select
                  id="country"
                  labelText="Country *"
                  value={formData.country}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    updateFormData('country', e.target.value)
                  }
                >
                  <SelectItem value="USA" text="United States" />
                  <SelectItem value="Canada" text="Canada" />
                </Select>
              </div>

              <div className="obs-field">
                <TextInput
                  id="load-capacity"
                  type="number"
                  labelText="Load Capacity (kW) *"
                  value={formData.loadCapacity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('loadCapacity', e.target.value)
                  }
                  placeholder="e.g., 5000"
                />
              </div>

              <div className="obs-field obs-full">
                <TextInput
                  id="utility-provider"
                  labelText="Utility Provider *"
                  value={formData.utilityProvider}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('utilityProvider', e.target.value)
                  }
                  placeholder="e.g., Austin Energy"
                />
              </div>

              <div className="obs-field obs-full">
                <TextInput
                  id="utility-account"
                  labelText="Utility Account Number"
                  value={formData.utilityAccountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('utilityAccountNumber', e.target.value)
                  }
                  placeholder="e.g., 123456789"
                />
              </div>
            </div>

            <div className="obs-section-divider" />

            <h3 className="obs-section-title">Service Level Agreement (SLA)</h3>
            <div className="obs-form-grid">
              <div className="obs-field">
                <TextInput
                  id="sla-uptime"
                  type="number"
                  labelText="Uptime Percentage *"
                  helperText="Target uptime percentage (e.g., 99.99%)"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.slaUptimePercentage}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFormData('slaUptimePercentage', e.target.value)
                  }
                  placeholder="e.g., 99.99"
                />
              </div>

              <div className="obs-field obs-full">
                <div className="obs-doc-label">
                  <span>Upload Full SLA Document *</span>
                  <span className="obs-format-hint">PDF/DOC/DOCX</span>
                </div>
                {renderSingleFileField('slaDocument', ['.pdf', '.doc', '.docx'])}
                <p className="obs-help-text">
                  Complete SLA document with uptime targets, response times, maintenance windows,
                  and penalties
                </p>
              </div>
            </div>
          </div>
        );

      case 1: // People
        return (
          <div className="obs-step">
            <h2 className="obs-step-title">User Access Management</h2>
            <p className="obs-step-desc">
              Add new users or grant existing users access to this site
            </p>

            <div className="obs-people">
              <div className="obs-search-section">
                <Search
                  id="user-search"
                  size="lg"
                  labelText="Search Users"
                  placeholder="Search by name, email, or role..."
                  value={userSearchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setUserSearchQuery(e.target.value)
                  }
                  onClear={() => setUserSearchQuery('')}
                />
                {loadingUsers && <p className="obs-info-note">Loading users...</p>}
              </div>

              {(selectedExistingUsers.length > 0 || formData.newUsers.length > 0) && (
                <div className="obs-selected-users">
                  <div className="obs-section-header">
                    <h3 className="obs-section-title">
                      Selected Users ({selectedExistingUsers.length + formData.newUsers.length})
                    </h3>
                    <Button kind="tertiary" size="sm" renderIcon={Add} onClick={addNewUser}>
                      Create New User
                    </Button>
                  </div>

                  <div className="obs-user-cards">
                    {selectedExistingUsers.map(user => (
                      <Tile key={`existing-${user.id}`} className="obs-user-card">
                        <div className="obs-user-card-header">
                          <Tag type="blue" size="sm">
                            Existing User
                          </Tag>
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            iconDescription="Remove from selection"
                            renderIcon={Close}
                            onClick={() => removeExistingUser(user.id)}
                          />
                        </div>
                        <div className="obs-user-card-field">
                          <TextInput
                            id={`existing-name-${user.id}`}
                            labelText="Name"
                            value={
                              `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'
                            }
                            readOnly
                          />
                        </div>
                        <div className="obs-user-card-field">
                          <TextInput
                            id={`existing-email-${user.id}`}
                            labelText="Email"
                            value={user.email}
                            readOnly
                          />
                        </div>
                        <div className="obs-user-card-field">
                          <TextInput
                            id={`existing-role-${user.id}`}
                            labelText="Role"
                            value={user.role}
                            readOnly
                          />
                        </div>
                      </Tile>
                    ))}

                    {formData.newUsers.map(user => (
                      <Tile key={`new-${user.id}`} className="obs-user-card">
                        <div className="obs-user-card-header">
                          <Tag type="teal" size="sm">
                            New User
                          </Tag>
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            iconDescription="Remove user"
                            renderIcon={Close}
                            onClick={() => removeNewUser(user.id)}
                          />
                        </div>
                        <div className="obs-user-card-field">
                          <TextInput
                            id={`new-email-${user.id}`}
                            type="email"
                            labelText="Email *"
                            placeholder="user@example.com"
                            value={user.email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateNewUser(user.id, 'email', e.target.value)
                            }
                          />
                        </div>
                        <div className="obs-user-card-field">
                          <TextInput
                            id={`new-first-${user.id}`}
                            labelText="First Name *"
                            placeholder="First name"
                            value={user.firstName || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateNewUser(user.id, 'firstName', e.target.value)
                            }
                          />
                        </div>
                        <div className="obs-user-card-field">
                          <TextInput
                            id={`new-last-${user.id}`}
                            labelText="Last Name *"
                            placeholder="Last name"
                            value={user.lastName || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateNewUser(user.id, 'lastName', e.target.value)
                            }
                          />
                        </div>
                        <div className="obs-user-card-field">
                          <Select
                            id={`new-role-${user.id}`}
                            labelText="Role *"
                            value={user.role}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              updateNewUser(user.id, 'role', e.target.value)
                            }
                          >
                            <SelectItem value="operator" text="Operator" />
                            <SelectItem value="operations_manager" text="Operations Manager" />
                            <SelectItem value="admin" text="Admin" />
                            <SelectItem value="executive" text="Executive" />
                          </Select>
                        </div>
                      </Tile>
                    ))}
                  </div>
                </div>
              )}

              {selectedExistingUsers.length === 0 && formData.newUsers.length === 0 && (
                <div className="obs-empty-selection">
                  <p className="obs-info-note">
                    No users selected yet. Add existing users from below or create new ones.
                  </p>
                  <Button kind="primary" size="md" renderIcon={Add} onClick={addNewUser}>
                    Create New User
                  </Button>
                </div>
              )}

              <div className="obs-available-users">
                <div className="obs-section-header">
                  <h3 className="obs-section-title">Available Users</h3>
                  <span className="obs-count">{displayedUsers.length} users</span>
                </div>
                <p className="obs-info-note">Click on a user to grant them access to this site</p>

                {displayedUsers.length === 0 ? (
                  <EmptyState
                    big={
                      userSearchQuery ? 'No users found matching your search' : 'No available users'
                    }
                  />
                ) : (
                  <div className="obs-user-list">
                    {displayedUsers.map(user => (
                      <button
                        key={user.id}
                        type="button"
                        className="obs-user-row"
                        onClick={() => addExistingUser(user)}
                      >
                        <div className="obs-user-info">
                          <strong>
                            {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No Name'}
                          </strong>
                          <span className="obs-user-email">{user.email}</span>
                        </div>
                        <Tag type="blue" size="sm">
                          {user.role}
                        </Tag>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 2: // Ontology
        return (
          <div className="obs-step">
            <h2 className="obs-step-title">Ontology &amp; Documentation</h2>
            <p className="obs-step-desc">
              Upload all structural, electrical, mechanical, and operational documentation for
              comprehensive knowledge graph generation
            </p>

            <Accordion className="obs-ontology">
              <AccordionItem
                open={ontologyExpanded.knowledgeGraph}
                onHeadingClick={() => toggleOntologySection('knowledgeGraph')}
                title={
                  <span className="obs-acc-title">
                    Knowledge Graph Source
                    <Tag type="blue" size="sm">
                      Optional
                    </Tag>
                  </span>
                }
              >
                <p className="obs-section-desc">
                  Visual representation of the site for automated knowledge graph generation
                </p>
                <div className="obs-doc-group">
                  <div className="obs-doc-label">
                    <span>Site Map</span>
                    <span className="obs-format-hint">PDF/PNG/JPG/SVG</span>
                  </div>
                  {renderSingleFileField('mapFile', ['.pdf', '.png', '.jpg', '.jpeg', '.svg'])}
                </div>
              </AccordionItem>

              <AccordionItem
                open={ontologyExpanded.electrical}
                onHeadingClick={() => toggleOntologySection('electrical')}
                title={
                  <span className="obs-acc-title">
                    Electrical Systems
                    <Tag type="red" size="sm">
                      Mandatory
                    </Tag>
                  </span>
                }
              >
                <p className="obs-section-desc">
                  Electrical topology and power distribution documentation
                </p>

                <div className="obs-doc-group">
                  <div className="obs-doc-label">
                    <span>Electrical One-Line Diagrams *</span>
                    <span className="obs-format-hint">DWG/DXF/PDF</span>
                  </div>
                  {renderMultiFileField('electricalOneLine', ['.dwg', '.dxf', '.pdf'])}
                </div>

                <div className="obs-doc-group">
                  <div className="obs-doc-label">
                    <span>Bill of Materials</span>
                    <span className="obs-format-hint">CSV/XLSX</span>
                  </div>
                  {renderSingleFileField('electricalBOM', ['.csv', '.xlsx'])}
                </div>

                <div className="obs-doc-group">
                  <div className="obs-doc-label">
                    <span>Panel Schedules</span>
                    <span className="obs-format-hint">PDF/XLSX</span>
                  </div>
                  {renderMultiFileField('panelSchedules', ['.pdf', '.xlsx'])}
                </div>
              </AccordionItem>

              <AccordionItem
                open={ontologyExpanded.mechanical}
                onHeadingClick={() => toggleOntologySection('mechanical')}
                title={
                  <span className="obs-acc-title">
                    Mechanical Systems
                    <Tag type="red" size="sm">
                      Mandatory
                    </Tag>
                  </span>
                }
              >
                <p className="obs-section-desc">Physical layout and HVAC system documentation</p>

                <div className="obs-doc-group">
                  <div className="obs-doc-label">
                    <span>Mechanical Layout *</span>
                    <span className="obs-format-hint">DWG/DXF/PDF</span>
                  </div>
                  {renderMultiFileField('mechanicalLayout', ['.dwg', '.dxf', '.pdf'])}
                </div>

                <div className="obs-doc-group">
                  <div className="obs-doc-label">
                    <span>HVAC Details *</span>
                    <span className="obs-format-hint">DWG/DXF/PDF</span>
                  </div>
                  {renderMultiFileField('hvacDetails', ['.dwg', '.dxf', '.pdf'])}
                </div>
              </AccordionItem>

              <AccordionItem
                open={ontologyExpanded.operational}
                onHeadingClick={() => toggleOntologySection('operational')}
                title={
                  <span className="obs-acc-title">
                    Operational Procedures
                    <Tag type="red" size="sm">
                      Mandatory
                    </Tag>
                  </span>
                }
              >
                <p className="obs-section-desc">Standard operating and maintenance procedures</p>

                <div className="obs-doc-group">
                  <div className="obs-doc-label">
                    <span>Standard Operating Procedures (SOPs) *</span>
                    <span className="obs-format-hint">PDF/DOCX (Multiple files)</span>
                  </div>
                  {renderMultiFileField('sops', ['.pdf', '.doc', '.docx'])}
                </div>

                <div className="obs-doc-group">
                  <div className="obs-doc-label">
                    <span>Maintenance Procedures</span>
                    <span className="obs-format-hint">PDF/XLSX (Preferred)</span>
                  </div>
                  {renderMultiFileField('maintenanceProcedures', [
                    '.pdf',
                    '.xlsx',
                    '.doc',
                    '.docx',
                  ])}
                </div>
              </AccordionItem>
            </Accordion>

            <div className="obs-info-box">
              <h4>Knowledge Graph Generation</h4>
              <p>
                All uploaded documents will be analyzed to automatically generate a comprehensive
                knowledge graph showing equipment relationships, zones, connectivity, and
                operational procedures. This enables:
              </p>
              <ul>
                <li>Impact analysis for maintenance and changes</li>
                <li>Agent assignment and autonomous monitoring</li>
                <li>SOP binding and workflow automation</li>
                <li>Cooling optimization and energy management</li>
              </ul>
            </div>
          </div>
        );

      case 3: // Telemetry
        return (
          <div className="obs-step">
            <h2 className="obs-step-title">Telemetry Configuration</h2>
            <p className="obs-step-desc">
              Configure data dumps or edge devices for telemetry collection
            </p>

            <div className="obs-section-header">
              <h3 className="obs-section-title">Data Sources</h3>
              <Button kind="tertiary" size="sm" renderIcon={Add} onClick={addTelemetrySource}>
                Add Source
              </Button>
            </div>

            {formData.telemetrySources.length === 0 ? (
              <EmptyState big="No telemetry sources configured yet" />
            ) : (
              <div className="obs-dyn-list">
                {formData.telemetrySources.map(source => (
                  <Tile key={source.id} className="obs-dyn-row">
                    <div className="obs-dyn-fields">
                      <Select
                        id={`tel-type-${source.id}`}
                        labelText="Source Type"
                        value={source.type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          updateTelemetrySource(source.id, 'type', e.target.value)
                        }
                      >
                        <SelectItem value="dump" text="Data Dump" />
                        <SelectItem value="edge" text="Edge Device" />
                        <SelectItem value="api" text="API Endpoint" />
                        <SelectItem value="mqtt" text="MQTT Broker" />
                      </Select>

                      <TextInput
                        id={`tel-name-${source.id}`}
                        labelText="Source Name"
                        placeholder="e.g., BMS System"
                        value={source.name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateTelemetrySource(source.id, 'name', e.target.value)
                        }
                      />

                      <TextInput
                        id={`tel-endpoint-${source.id}`}
                        labelText="Endpoint/URL"
                        placeholder="e.g., https://bms.example.com/api"
                        value={source.endpoint}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateTelemetrySource(source.id, 'endpoint', e.target.value)
                        }
                      />

                      <Select
                        id={`tel-protocol-${source.id}`}
                        labelText="Protocol"
                        value={source.protocol}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          updateTelemetrySource(source.id, 'protocol', e.target.value)
                        }
                      >
                        <SelectItem value="https" text="HTTPS" />
                        <SelectItem value="http" text="HTTP" />
                        <SelectItem value="mqtt" text="MQTT" />
                        <SelectItem value="modbus" text="Modbus" />
                      </Select>
                    </div>
                    <div className="obs-dyn-actions">
                      <Button
                        kind="danger--ghost"
                        size="sm"
                        renderIcon={TrashCan}
                        onClick={() => removeTelemetrySource(source.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </Tile>
                ))}
              </div>
            )}
          </div>
        );

      case 4: // Checks
        return (
          <div className="obs-step">
            <h2 className="obs-step-title">Configuration Checks</h2>
            <p className="obs-step-desc">
              Provide additional details about power generation and optimization preferences
            </p>

            <div className="obs-checks">
              <div className="obs-checks-block">
                <div className="obs-section-header">
                  <h3 className="obs-section-title">Onsite Generation</h3>
                  <Button kind="tertiary" size="sm" renderIcon={Add} onClick={addOnsiteGeneration}>
                    Add Generation Source
                  </Button>
                </div>

                {formData.onsiteGeneration.length === 0 ? (
                  <EmptyState
                    big="No onsite generation sources added yet."
                    sub='Click "Add Generation Source" to add solar, wind, BESS, or other power sources.'
                  />
                ) : (
                  <div className="obs-dyn-list">
                    {formData.onsiteGeneration.map((gen, index) => (
                      <Tile key={gen.id} className="obs-dyn-row">
                        <div className="obs-dyn-head">
                          <span className="obs-dyn-num">#{index + 1}</span>
                          <Button
                            kind="danger--ghost"
                            size="sm"
                            hasIconOnly
                            iconDescription="Remove this generation source"
                            renderIcon={TrashCan}
                            onClick={() => removeOnsiteGeneration(gen.id)}
                          />
                        </div>
                        <div className="obs-dyn-fields">
                          <Select
                            id={`gen-type-${gen.id}`}
                            labelText="Generation Type *"
                            value={gen.type}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              updateOnsiteGeneration(gen.id, 'type', e.target.value)
                            }
                          >
                            <SelectItem value="" text="Select type..." />
                            <SelectItem value="solar" text="Solar PV" />
                            <SelectItem value="wind" text="Wind" />
                            <SelectItem value="diesel" text="Diesel Generator" />
                            <SelectItem value="natural-gas" text="Natural Gas" />
                            <SelectItem value="bess" text="Battery Energy Storage (BESS)" />
                            <SelectItem value="fuel-cell" text="Fuel Cell" />
                            <SelectItem value="hybrid" text="Hybrid System" />
                          </Select>
                          <TextInput
                            id={`gen-cap-${gen.id}`}
                            type="number"
                            labelText="Capacity (kW) *"
                            min="0"
                            step="0.01"
                            value={gen.capacityKW}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateOnsiteGeneration(gen.id, 'capacityKW', e.target.value)
                            }
                            placeholder="e.g., 500"
                          />
                        </div>
                      </Tile>
                    ))}
                  </div>
                )}
              </div>

              <div className="obs-section-divider" />

              <div className="obs-checks-block">
                <div className="obs-section-header">
                  <h3 className="obs-section-title">Grid Connections</h3>
                  <Button kind="tertiary" size="sm" renderIcon={Add} onClick={addGridConnection}>
                    Add Grid Connection
                  </Button>
                </div>

                {formData.gridConnections.length === 0 ? (
                  <EmptyState
                    big="No grid connections added yet."
                    sub='Click "Add Grid Connection" to specify utility connections and their rated capacities.'
                  />
                ) : (
                  <div className="obs-dyn-list">
                    {formData.gridConnections.map((conn, index) => (
                      <Tile key={conn.id} className="obs-dyn-row">
                        <div className="obs-dyn-head">
                          <span className="obs-dyn-num">Connection #{index + 1}</span>
                          <Button
                            kind="danger--ghost"
                            size="sm"
                            hasIconOnly
                            iconDescription="Remove this grid connection"
                            renderIcon={TrashCan}
                            onClick={() => removeGridConnection(conn.id)}
                          />
                        </div>
                        <div className="obs-dyn-fields">
                          <TextInput
                            id={`grid-name-${conn.id}`}
                            labelText="Connection Name *"
                            value={conn.name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateGridConnection(conn.id, 'name', e.target.value)
                            }
                            placeholder="e.g., Primary Feed A"
                          />
                          <TextInput
                            id={`grid-cap-${conn.id}`}
                            type="number"
                            labelText="Rated Capacity (kW) *"
                            min="0"
                            step="0.01"
                            value={conn.capacityKW}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateGridConnection(conn.id, 'capacityKW', e.target.value)
                            }
                            placeholder="e.g., 2000"
                          />
                        </div>
                      </Tile>
                    ))}
                  </div>
                )}
              </div>

              <div className="obs-section-divider" />

              <div className="obs-checks-block">
                <CheckboxGroup legendText="Optimization Goals" className="obs-checkbox-group">
                  <Checkbox
                    id="goal-cost"
                    labelText="Cost Minimization"
                    checked={formData.optimizationGoals.includes('cost')}
                    onChange={(
                      _e: React.ChangeEvent<HTMLInputElement>,
                      { checked }: { checked: boolean }
                    ) => toggleOptimizationGoal('cost', checked)}
                  />
                  <Checkbox
                    id="goal-carbon"
                    labelText="Carbon Reduction"
                    checked={formData.optimizationGoals.includes('carbon')}
                    onChange={(
                      _e: React.ChangeEvent<HTMLInputElement>,
                      { checked }: { checked: boolean }
                    ) => toggleOptimizationGoal('carbon', checked)}
                  />
                  <Checkbox
                    id="goal-efficiency"
                    labelText="Energy Efficiency"
                    checked={formData.optimizationGoals.includes('efficiency')}
                    onChange={(
                      _e: React.ChangeEvent<HTMLInputElement>,
                      { checked }: { checked: boolean }
                    ) => toggleOptimizationGoal('efficiency', checked)}
                  />
                  <Checkbox
                    id="goal-demand"
                    labelText="Demand Response"
                    checked={formData.optimizationGoals.includes('demand')}
                    onChange={(
                      _e: React.ChangeEvent<HTMLInputElement>,
                      { checked }: { checked: boolean }
                    ) => toggleOptimizationGoal('demand', checked)}
                  />
                  <Checkbox
                    id="goal-reliability"
                    labelText="Grid Reliability"
                    checked={formData.optimizationGoals.includes('reliability')}
                    onChange={(
                      _e: React.ChangeEvent<HTMLInputElement>,
                      { checked }: { checked: boolean }
                    ) => toggleOptimizationGoal('reliability', checked)}
                  />
                </CheckboxGroup>
              </div>
            </div>
          </div>
        );

      case 5: // Confirm
        return (
          <div className="obs-step">
            <h2 className="obs-step-title">Confirm Configuration</h2>
            <p className="obs-step-desc">Review all settings before submitting</p>

            <div className="obs-review">
              <Tile className="obs-review-group">
                <h3 className="obs-section-title">Overview</h3>
                <div className="obs-review-grid">
                  <div className="obs-review-k">Name</div>
                  <div className="obs-review-v">{formData.name || 'Not provided'}</div>
                  <div className="obs-review-k">Site Key</div>
                  <div className="obs-review-v">{formData.siteKey || 'Not provided'}</div>
                  <div className="obs-review-k">Region</div>
                  <div className="obs-review-v">
                    {regions.find(r => r.regionId === formData.regionId)?.label || 'Not selected'}
                  </div>
                  <div className="obs-review-k">Redundancy Level</div>
                  <div className="obs-review-v">{formData.redundancyLevel || 'Not provided'}</div>
                  <div className="obs-review-k">Address</div>
                  <div className="obs-review-v">
                    {formData.address &&
                    formData.city &&
                    formData.state &&
                    formData.zipCode &&
                    formData.country
                      ? `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}, ${formData.country}`
                      : 'Not provided'}
                  </div>
                  <div className="obs-review-k">Load Capacity</div>
                  <div className="obs-review-v">
                    {formData.loadCapacity ? `${formData.loadCapacity} kW` : 'Not provided'}
                  </div>
                  <div className="obs-review-k">Utility Provider</div>
                  <div className="obs-review-v">{formData.utilityProvider || 'Not provided'}</div>
                  <div className="obs-review-k">SLA Uptime</div>
                  <div className="obs-review-v">
                    {formData.slaUptimePercentage
                      ? `${formData.slaUptimePercentage}%`
                      : 'Not provided'}
                  </div>
                  <div className="obs-review-k">SLA Document</div>
                  <div className="obs-review-v">
                    {formData.slaDocument ? formData.slaDocument.name : 'Not uploaded'}
                  </div>
                </div>
              </Tile>

              <Tile className="obs-review-group">
                <h3 className="obs-section-title">People</h3>
                <div className="obs-review-grid">
                  <div className="obs-review-k">New Users</div>
                  <div className="obs-review-v">
                    {formData.newUsers.length > 0
                      ? formData.newUsers
                          .map(u => `${u.firstName} ${u.lastName} (${u.email}) - ${u.role}`)
                          .join(', ')
                      : 'None'}
                  </div>
                  <div className="obs-review-k">Existing Users</div>
                  <div className="obs-review-v">
                    {selectedExistingUsers.length > 0
                      ? selectedExistingUsers
                          .map(u => `${u.first_name} ${u.last_name} (${u.email}) - ${u.role}`)
                          .join(', ')
                      : 'None'}
                  </div>
                </div>
              </Tile>

              <Tile className="obs-review-group">
                <h3 className="obs-section-title">Ontology &amp; Documentation</h3>
                <div className="obs-review-grid">
                  <div className="obs-review-k">Knowledge Graph Map</div>
                  <div className="obs-review-v">
                    {formData.mapFile ? formData.mapFile.name : 'Not uploaded'}
                  </div>
                  <div className="obs-review-k">Electrical One-Line</div>
                  <div className="obs-review-v">
                    {formData.electricalOneLine.length > 0
                      ? `${formData.electricalOneLine.length} file(s)`
                      : 'Not uploaded'}
                  </div>
                  <div className="obs-review-k">Electrical BOM</div>
                  <div className="obs-review-v">
                    {formData.electricalBOM ? formData.electricalBOM.name : 'Not uploaded'}
                  </div>
                  <div className="obs-review-k">Panel Schedules</div>
                  <div className="obs-review-v">
                    {formData.panelSchedules.length > 0
                      ? `${formData.panelSchedules.length} file(s)`
                      : 'Not uploaded'}
                  </div>
                  <div className="obs-review-k">Mechanical Layout</div>
                  <div className="obs-review-v">
                    {formData.mechanicalLayout.length > 0
                      ? `${formData.mechanicalLayout.length} file(s)`
                      : 'Not uploaded'}
                  </div>
                  <div className="obs-review-k">HVAC Details</div>
                  <div className="obs-review-v">
                    {formData.hvacDetails.length > 0
                      ? `${formData.hvacDetails.length} file(s)`
                      : 'Not uploaded'}
                  </div>
                  <div className="obs-review-k">SOPs</div>
                  <div className="obs-review-v">
                    {formData.sops.length > 0 ? `${formData.sops.length} file(s)` : 'Not uploaded'}
                  </div>
                  <div className="obs-review-k">Maintenance Procedures</div>
                  <div className="obs-review-v">
                    {formData.maintenanceProcedures.length > 0
                      ? `${formData.maintenanceProcedures.length} file(s)`
                      : 'Not uploaded'}
                  </div>
                </div>
              </Tile>

              <Tile className="obs-review-group">
                <h3 className="obs-section-title">Telemetry</h3>
                <div className="obs-review-grid">
                  <div className="obs-review-k">Data Sources</div>
                  <div className="obs-review-v">
                    {formData.telemetrySources.length} source(s) configured
                  </div>
                </div>
              </Tile>

              <Tile className="obs-review-group">
                <h3 className="obs-section-title">Checks</h3>
                <div className="obs-review-grid">
                  <div className="obs-review-k">Onsite Generation</div>
                  <div className="obs-review-v">
                    {formData.onsiteGeneration.length > 0
                      ? formData.onsiteGeneration
                          .map(
                            (gen, idx) =>
                              `${idx + 1}. ${gen.type || 'Unknown'}: ${gen.capacityKW || '0'} kW`
                          )
                          .join(' | ')
                      : 'None'}
                  </div>
                  <div className="obs-review-k">Grid Connections</div>
                  <div className="obs-review-v">
                    {formData.gridConnections.length > 0
                      ? formData.gridConnections
                          .map(
                            (conn, idx) =>
                              `${idx + 1}. ${conn.name || 'Unnamed'}: ${conn.capacityKW || '0'} kW`
                          )
                          .join(' | ')
                      : 'None'}
                  </div>
                  <div className="obs-review-k">Optimization Goals</div>
                  <div className="obs-review-v">
                    {formData.optimizationGoals.length > 0
                      ? formData.optimizationGoals.join(', ')
                      : 'None selected'}
                  </div>
                </div>
              </Tile>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="obs-container">
      {/* Stepper */}
      <div className="obs-stepper">
        <ProgressIndicator
          currentIndex={currentStep}
          spaceEqually
          onChange={handleStepJump}
          className="obs-progress"
        >
          {steps.map((step, index) => (
            <ProgressStep
              key={step.id}
              label={step.name}
              complete={index < currentStep}
              current={index === currentStep}
              disabled={index > currentStep + 1}
            />
          ))}
        </ProgressIndicator>
      </div>

      {/* Content */}
      <div className="obs-content">{renderStepContent()}</div>

      {/* Validation / error notification */}
      {notice && (
        <InlineNotification
          className="obs-notice"
          kind={notice.kind}
          title={notice.title}
          subtitle={notice.subtitle}
          lowContrast
          onCloseButtonClick={() => setNotice(null)}
        />
      )}

      {/* Navigation */}
      <div className="obs-nav">
        <div className="obs-nav-left">
          <Button kind="secondary" onClick={() => navigate('/admin')}>
            Cancel
          </Button>
          <div className="obs-save-status">
            {isSaving && <InlineLoading description="Saving..." />}
            {lastSaved && !isSaving && (
              <span className="obs-last-saved">Saved {formatLastSaved(lastSaved)}</span>
            )}
          </div>
        </div>
        <div className="obs-nav-buttons">
          <Button kind="ghost" onClick={handleSaveAndExit}>
            Save &amp; Exit
          </Button>
          {currentStep > 0 && (
            <Button kind="secondary" renderIcon={ArrowLeft} onClick={handlePrevious}>
              Previous
            </Button>
          )}
          {currentStep < steps.length - 1 ? (
            <Button kind="primary" renderIcon={ArrowRight} onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button kind="primary" onClick={handleSubmit}>
              Submit Onboarding
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardSite;
